import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
} from '@mui/material';
import {
  Link as LinkIcon,
  SlidersHorizontal,
  Rocket,
  ArrowRight,
  Check,
} from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

const steps = [
  {
    number: '01',
    title: 'Connect',
    description: 'Link your phone, Instagram, Facebook, WhatsApp, Telegram, web, and email in 2 clicks with secure OAuth. No code needed.',
    icon: LinkIcon,
    visual: (
      <Box sx={{ display: 'flex', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
        {['Phone', 'Instagram', 'WhatsApp', 'Email'].map((p) => (
          <Box
            key={p}
            sx={{
              px: 1.4,
              py: 0.6,
              borderRadius: '999px',
              bgcolor: tokens.bgDefault,
              border: `1px solid ${tokens.border}`,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary' }}>
              {p}
            </Typography>
          </Box>
        ))}
      </Box>
    ),
  },
  {
    number: '02',
    title: 'Train',
    description: 'Upload docs, point at your website, paste FAQs. Your AI learns your business — products, policies, voice — in minutes.',
    icon: SlidersHorizontal,
    visual: (
      <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['Personality', 'Documents', 'Website', 'Q&A'].map((t) => (
          <Box
            key={t}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.4,
              py: 0.6,
              borderRadius: '999px',
              bgcolor: tokens.copperTint,
              border: '1px solid rgba(199, 91, 57, 0.25)',
              color: tokens.copperDark,
            }}
          >
            <Check size={12} strokeWidth={2} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: tokens.copperDark }}>
              {t}
            </Typography>
          </Box>
        ))}
      </Box>
    ),
  },
  {
    number: '03',
    title: 'Run',
    description: 'Your AI sells, supports, and organizes 24/7 with full oversight. Review-before-send mode for high-stakes replies, auto for everything else. Human handoff one tap away.',
    icon: Rocket,
    visual: (
      <Box
        sx={{
          mt: 2.5,
          px: 1.6,
          py: 1.2,
          borderRadius: `${tokens.radiusControl}px`,
          bgcolor: tokens.sageTint,
          border: '1px solid rgba(47, 93, 80, 0.25)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: tokens.sage,
              animation: 'cleoPulse 2.2s ease-in-out infinite',
              '@keyframes cleoPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.35 },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }}
          />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.sage }}>
            3 new leads captured today
          </Typography>
        </Box>
      </Box>
    ),
  },
];

export default function HowItWorks() {
  return (
    <Box
      id="how-it-works"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            How It Works
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Go Live in 5 Minutes
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
            No developers, no complex setup. Three steps to a working AI Business Manager.
          </Typography>
        </Reveal>

        <Grid container spacing={4}>
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Reveal delay={index * tokens.motion.stagger} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: `${tokens.radiusCard}px`,
                      bgcolor: tokens.bgPaper,
                      border: `1px solid ${tokens.border}`,
                      boxShadow: tokens.shadowSoft,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: `box-shadow ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                      '&:hover': {
                        borderColor: '#D9CDBC',
                        boxShadow: tokens.shadowLift,
                      },
                    }}
                  >
                    {/* ghost step numeral */}
                    <Typography
                      aria-hidden="true"
                      sx={{
                        position: 'absolute',
                        top: -18,
                        right: 8,
                        fontSize: '7rem',
                        fontWeight: 600,
                        fontFamily: tokens.fontDisplay,
                        opacity: 0.06,
                        lineHeight: 1,
                        color: tokens.ink,
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    >
                      {step.number}
                    </Typography>

                    {/* icon */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: `${tokens.radiusControl}px`,
                        bgcolor: tokens.copperTint,
                        color: tokens.copper,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                      }}
                    >
                      <Icon size={24} strokeWidth={1.5} />
                    </Box>

                    <Typography
                      variant="overline"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      Step {step.number}
                    </Typography>

                    <Typography variant="h3" component="h3" sx={{ mb: 1.5 }}>
                      {step.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {step.description}
                    </Typography>

                    {step.visual}
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>

        <Reveal sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            variant="outlined"
            endIcon={<ArrowRight size={18} strokeWidth={1.5} />}
            href="https://app.cleolead.com/login"
          >
            Start Free Trial
          </Button>
        </Reveal>
      </Container>
    </Box>
  );
}
